'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import {
  FilterLabel,
  FilterIcon,
  IncludeExcludeSelect,
  FilterAutocomplete,
  Divider,
  ClearFilterButton,
} from 'src/components/FilterSets/components';
import { SelectedFilters } from 'src/components/FilterSets/SelectedFilters';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import type { FilterValue } from 'src/context/reducers/filterSetReducer';
import { buildSocCombinedOptions, resolveSocSelection } from './utils';
import useSocOptions from './useSocOptions';

const SiteOfCare = ({
  location,
  smallLabel = false,
}: {
  location: string;
  smallLabel?: boolean;
}): React.ReactElement => {
  const { filterSet, updateFilter, removeFilterValue } =
    useFilterSets(location);
  const filterValue = filterSet.siteOfCare;

  const [searchTerm, setSearchTerm] = useState('');
  const { socOptions, isLoading } = useSocOptions();

  const options = useMemo(() => {
    const all = buildSocCombinedOptions(socOptions);
    const term = searchTerm.trim().toLowerCase();
    if (!term) return all;
    return all.filter((opt) => opt.label.toLowerCase().includes(term));
  }, [socOptions, searchTerm]);

  const handleFilterChange = (value: FilterValue) => {
    updateFilter('siteOfCare', {
      ...value,
      values: resolveSocSelection(
        filterValue?.values ?? [],
        value?.values ?? []
      ),
    });
  };

  const handleRemoveFilter = (filterLabel: string) => {
    removeFilterValue('siteOfCare', filterLabel);
  };

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const hasValues = filterValue?.values?.length;

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between">
        <FilterLabel smallLabel={smallLabel}>Site of Care Type</FilterLabel>
        <ClearFilterButton
          type="siteOfCare"
          isDisabled={!hasValues}
          location={location}
        />
      </div>
      <div className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-between items-center">
        <div className="flex-1 flex justify-start items-center gap-2">
          <FilterIcon icon={faLocationDot} />
          <IncludeExcludeSelect
            title="Site of Care"
            value={filterValue}
            onChange={handleFilterChange}
          />
          <Divider />
          <FilterAutocomplete
            options={options}
            value={filterValue}
            onChange={handleFilterChange}
            isLoading={isLoading}
            placeholder="Search site of care..."
            onSearch={handleSearch}
          />
        </div>
      </div>
      <SelectedFilters
        filters={filterValue?.values.map((v) => v.label)}
        onRemove={handleRemoveFilter}
        isAll={filterValue?.is_all}
      />
    </div>
  );
};

export default SiteOfCare;
